import { useEffect, useMemo, useRef, useState } from 'react';
import { isOutline } from '@/features/stock/libraryItems';
import { useQuery } from '@tanstack/react-query';
import {
  Maximize2,
  Minimize2,
  RefreshCw,
  AlertCircle,
  Wifi,
  WifiOff,
} from 'lucide-react';

import { useEventMonitorSocket } from './useEventMonitorSocket';
import { factsApi, type EventFacts } from '@/features/insights/factsApi';
import {
  fmtInt,
  goalOf,
  ledgerOf,
  paceOf,
  phaseOf,
  throughputOf,
  windowFraction,
} from '@/features/insights/engine';
import { GoalCounters } from '@/features/insights/charts/GoalCounters';
import { PrizeWall } from '@/features/insights/charts/PrizeWall';
import { ThroughputTimeline } from '@/features/insights/charts/ThroughputTimeline';
import { cn } from '@/lib/cn';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { Badge } from '@/components/ui/Badge';
import { useT } from '@/lib/i18n/useT';

/**
 * Caso 10 — Live monitor for an event in flight.
 *
 * v1 reuses `/xk-event/:id/report` with a 5-second `refetchInterval` (polling)
 * — the WebSocket gateway + sticky-sessions piece is gated on the infra
 * checklist (`docs-pend/CHECKLIST-INFRA.md`). Switching to push later only
 * requires changing the queryFn; the UI stays.
 *
 * Mode: fullscreen-friendly, big tipo, alto contraste. Designed to be
 * shown on a TV at the event venue.
 */
/**
 * Task 5.2 — the live monitor IS the Track tab, not a separate page.
 *
 * It used to live at `/events/:id/live`, reachable only via a button on a Track
 * tab that otherwise held a goal editor. Track now shows current state and
 * nothing else, and this renders inside it. The fullscreen toggle it already
 * had is what makes it a venue screen: expanded Track, same component.
 */
export function LiveMonitor({ eventId }: { eventId: number }) {
  const t = useT();
  const [fullscreen, setFullscreen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);

  // Caso 10 v2 — `queryKey` is the canonical handle for the live monitor
  // data; the socket hook invalidates it on every `monitor:tick`. We keep
  // a 5s polling fallback in case the WebSocket connect fails (sticky
  // sessions still pending per CHECKLIST-INFRA.md).
  const queryKey = useMemo(() => ['liveMonitor', eventId], [eventId]);
  // Los hechos del evento son lo que Track cuenta: contadores, muro de
  // premios y throughput salen del mismo sitio que el informe, así que un
  // número visto en directo es el mismo que se leerá al cierre.
  const report = useQuery({
    queryKey,
    queryFn: () => factsApi.forEvents([eventId]),
    enabled: Number.isFinite(eventId) && eventId > 0,
    refetchInterval: 5_000,
  });

  const { connected } = useEventMonitorSocket(eventId, queryKey);
  const facts = report.data?.events[0];
  const now = Date.now();
  const phase = facts ? phaseOf(facts, now) : 'edit';
  const ledger = useMemo(() => (facts ? ledgerOf(facts.ledger) : null), [facts]);
  // `now` cambia en cada render; el throughput sólo importa por el bloque en
  // curso, así que basta con recalcularlo cuando llegan hechos nuevos.
  const throughput = useMemo(() => (facts ? throughputOf(facts.throughput, Date.now()) : []), [facts]);
  const live = phase === 'live';
  const frac = facts ? windowFraction(facts.event.startDate, facts.event.endDate, now) : null;

  // Track Fullscreen API state so the button label can swap.
  useEffect(() => {
    const onChange = () => setFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  function toggleFullscreen() {
    if (document.fullscreenElement) {
      document.exitFullscreen().catch(() => undefined);
    } else {
      rootRef.current?.requestFullscreen?.().catch(() => undefined);
    }
  }

  if (!Number.isFinite(eventId) || eventId <= 0) {
    return <p className="text-sm text-[var(--color-fg-muted)]">{t('liveMonitor.invalidId')}</p>;
  }

  return (
    <div
      ref={rootRef}
      className={
        'rounded-xl bg-[#0e0e0e] text-white ' +
        (fullscreen ? 'min-h-screen rounded-none' : '')
      }
    >
      {/* Header — hidden in fullscreen so the TV view is just the data. */}
      {!fullscreen && (
        <header className="flex items-center justify-between gap-3 border-b border-white/10 px-6 py-4">
          <span className="text-sm opacity-70">{t('liveMonitor.title')}</span>
          <div className="flex items-center gap-2">
            {/* Caso 10 v2 — transport indicator: green when the WS is up
                (push), neutral when only polling is keeping the view
                warm. */}
            <Badge tone={connected ? 'success' : 'neutral'}>
              {connected ? (
                <Wifi className="mr-1 inline h-3 w-3" />
              ) : (
                <WifiOff className="mr-1 inline h-3 w-3" />
              )}
              {connected
                ? t('liveMonitor.transport.live')
                : t('liveMonitor.transport.polling')}
            </Badge>
            <Badge tone={report.isFetching ? 'accent' : 'neutral'}>
              <RefreshCw
                className={
                  'mr-1 inline h-3 w-3 ' +
                  (report.isFetching ? 'animate-spin' : '')
                }
              />
              {report.isFetching
                ? t('liveMonitor.refreshing')
                : t('liveMonitor.live')}
            </Badge>
            <Button variant="ghost" onClick={toggleFullscreen}>
              {fullscreen ? (
                <Minimize2 className="h-4 w-4" />
              ) : (
                <Maximize2 className="h-4 w-4" />
              )}
            </Button>
          </div>
        </header>
      )}

      <main className="px-6 py-8 lg:px-10 lg:py-10">
        {report.isLoading || !facts || !ledger ? (
          <Skeleton className="h-40" />
        ) : (
          <div className="space-y-6">
            <div>
              <Badge tone="accent">{facts.event.name}</Badge>
              <h1 className="mt-2 text-4xl font-bold lg:text-5xl">{facts.event.name}</h1>
            </div>

            {phase === 'upcoming' ? (
              /* Antes del evento no hay nada que contar: se comprueba que
                 todo lo que hará falta para contar está en su sitio. Un
                 contador vacío es un pendiente, no un cero. */
              <Readiness facts={facts} missingCost={ledger.missingCost.length} />
            ) : (
              <>
                <PrizeWall
                  rows={ledger.rows}
                  header={
                    <span className="rounded-full bg-[color-mix(in_srgb,var(--color-warn)_16%,transparent)] px-3 py-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-warn)]">
                      {throughput.length && live
                        ? t('liveMonitor.block', { n: fmtInt(throughput[throughput.length - 1]!.blocks.find((b) => b.live)?.count ?? 0), peak: fmtInt(throughput[throughput.length - 1]!.peak) })
                        : t('liveMonitor.inPlay', { n: fmtInt(ledger.inPlay), a: fmtInt(ledger.allocated) })}
                    </span>
                  }
                />

                <GoalCounters
                  counters={[
                    {
                      label: t('liveMonitor.kpi.interactions'),
                      pace: paceOf(facts.funnel.interactions, goalOf(facts, 'responses') ?? goalOf(facts, 'samples'), { frac, live }),
                      hint: t('liveMonitor.hint.interactions'),
                    },
                    {
                      label: t('liveMonitor.kpi.leads'),
                      pace: paceOf(facts.funnel.leads, goalOf(facts, 'leads'), { frac, live }),
                      hint: t('liveMonitor.hint.leads'),
                    },
                    {
                      label: t('liveMonitor.kpi.prizesDistributed'),
                      pace: paceOf(ledger.delivered, ledger.won, { mode: 'ratio' }),
                      hint: t('liveMonitor.hint.prizes', { inPlay: fmtInt(ledger.inPlay), allocated: fmtInt(ledger.allocated) }),
                      mode: 'ratio',
                    },
                  ]}
                />

                <PaceLine facts={facts} frac={frac} live={live} />

                {ledger.inPlay <= 5 && ledger.allocated > 0 && (
                  <div className="flex items-center gap-3 rounded-lg bg-[var(--color-warn-soft,rgba(251,191,36,0.15))] px-4 py-3 text-sm">
                    <AlertCircle className="h-5 w-5 text-[var(--color-warn,#FBBF24)]" />
                    <span>{t('liveMonitor.alert.lowStock', { count: ledger.inPlay })}</span>
                  </div>
                )}

                <ThroughputTimeline
                  series={throughput}
                  title={t('liveMonitor.throughput')}
                  claimRate={facts.funnel.interactions ? ledger.delivered / facts.funnel.interactions : 0}
                  compact
                />
                <p className="text-xs text-white/50">{t('liveMonitor.trackNote')}</p>
              </>
            )}
          </div>
        )}
      </main>
    </div>
  );
}

/** Las comprobaciones de la víspera: lo que hace falta para que Track cuente. */
function Readiness({ facts, missingCost }: { facts: EventFacts; missingCost: number }) {
  const t = useT();
  const checks = [
    { label: t('liveMonitor.ready.stations'), ok: facts.stations.total > 0 },
    { label: t('liveMonitor.ready.survey'), ok: facts.stations.withSurvey > 0 },
    { label: t('liveMonitor.ready.merch'), ok: facts.ledger.some((r) => r.allocated > 0) },
    { label: t('liveMonitor.ready.costs'), ok: facts.ledger.length > 0 && missingCost === 0 },
    { label: t('liveMonitor.ready.goals'), ok: facts.goals.length > 0 },
    // CH-14 — un premio con silueta de la biblioteca aún no tiene foto real.
    { label: t('liveMonitor.ready.photos'), ok: !facts.ledger.some((r) => isOutline(r.image)) },
  ];
  const clear = checks.filter((c) => c.ok).length;
  return (
    <section className="rounded-2xl border border-white/10 bg-white/5 p-5">
      <div className="mb-1 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-chrome-accent)]">
        {t('liveMonitor.ready.kicker')}
      </div>
      <h2 className="mb-4 text-2xl font-bold">{t('liveMonitor.ready.head', { n: clear, total: checks.length })}</h2>
      <ul className="divide-y divide-white/10">
        {checks.map((c) => (
          <li key={c.label} className="flex items-center gap-3 py-3">
            <span
              className={cn(
                'flex h-6 w-6 items-center justify-center rounded-full text-xs font-bold',
                c.ok ? 'bg-[var(--color-chrome-accent-soft)] text-[var(--color-chrome-accent)]' : 'bg-[color-mix(in_srgb,var(--color-error)_25%,transparent)] text-[#F0A9A7]',
              )}
            >
              {c.ok ? '✓' : '!'}
            </span>
            <span className="flex-1 text-sm font-semibold">{c.label}</span>
            <span className={cn('font-mono text-[10px] font-bold uppercase tracking-widest', c.ok ? 'text-[var(--color-chrome-accent)]' : 'text-[#F0A9A7]')}>
              {c.ok ? t('liveMonitor.ready.ok') : t('liveMonitor.ready.blocks')}
            </span>
          </li>
        ))}
      </ul>
      <p className="mt-3 text-xs text-white/50">{t('liveMonitor.ready.note')}</p>
    </section>
  );
}

/** La marca de ritmo en una frase: cuánto de la ventana ha corrido y cuánto del objetivo está dentro. */
function PaceLine({ facts, frac, live }: { facts: EventFacts; frac: number | null; live: boolean }) {
  const t = useT();
  const goal = goalOf(facts, 'leads');
  const inherited = facts.goals.find((g) => g.metric === 'leads')?.source === 'project';
  const leads = facts.funnel.leads;
  let text: string;
  if (!goal) text = t('liveMonitor.pace.noGoal');
  else if (live && frac != null) {
    const done = Math.round(frac * 100);
    const got = Math.round((leads / goal) * 100);
    text = t(leads / goal >= frac ? 'liveMonitor.pace.ahead' : 'liveMonitor.pace.behind', { done, got });
  } else text = t('liveMonitor.pace.closed', { n: fmtInt(leads), goal: fmtInt(goal) });
  if (goal && inherited) text += ' ' + t('liveMonitor.pace.inherited');
  return (
    <div className="flex flex-wrap items-center gap-3 rounded-xl border border-white/10 px-4 py-3">
      <span className="flex items-center gap-2 font-mono text-[10px] font-bold uppercase tracking-widest text-[var(--color-chrome-accent)]">
        <span className="h-3 w-0.5 bg-white/60" />
        {t('dash.pace.mark')}
      </span>
      <span className="text-sm text-white/80">{text}</span>
    </div>
  );
}
